import { useEffect, useContext } from 'react';
import { useAuth } from 'react-oidc-context';
import { useHistory } from 'react-router';
import type { AuthTokens } from '../../models/auth';
import { UserContext } from '../../providers/UserProvider';
import { signInWithXimiIdentity } from '../../stores/ximi/ximiStimulinSignIn';
import { ensureBeneficiariesSynced } from '../../stores/ximi/ximiBeneficiariesSync';
import { getAccessToken } from '../storage/tokenStorage';
import { XIMI_SSO_CALLBACK_PATH } from '../../routePaths';

/** `ximiError=1` reste le cas générique ; un code métier le remplace pour un message dédié. */
const ximiErrorRedirect = (code?: string) => `/login?ximiError=${code ?? '1'}`;

// Garde au niveau module : survit aux remounts du composant (contrairement à un useRef),
// bloque un 2e échange concurrent quand Router est démonté/remonté (Loader UserProvider…).
let ximiSignInInFlight = false;

/**
 * Au premier login SSO Ximi : échange l'identité contre les tokens Stimul'In, hydrate le user
 * et route vers /home. En cas d'échec, retour à /login avec un flag (bandeau d'erreur générique).
 * Skip si une session Stimul'In existe déjà (pas de ré-échange après login réussi).
 */
export function useXimiStimulinSignIn(): void {
    const auth = useAuth();
    const history = useHistory();
    const userData = useContext(UserContext);

    useEffect(() => {
        if (!auth.isAuthenticated || !auth.user || ximiSignInInFlight) return;
        ximiSignInInFlight = true; // synchrone → bloque un 2e run concurrent
        // Écran d'attente pendant l'échange : le web est déjà sur cette page ;
        // le natif quitte select-account (sinon elle resterait affichée sans feedback).
        history.replace(XIMI_SSO_CALLBACK_PATH);

        const run = async () => {
            try {
                if (await getAccessToken()) {
                    // Session Stimul'In déjà présente : pas de ré-échange, on route
                    // directement (sinon l'utilisateur resterait sur select-account).
                    history.replace('/home');
                    return;
                }
                const result = await signInWithXimiIdentity();
                if (!result.ok) {
                    history.replace(ximiErrorRedirect(result.code));
                    return;
                }
                const user = await userData?.refreshUser?.(result.tokens.accessToken);
                // Préchauffage du périmètre Xelya, en arrière-plan : si ça rate, l'écran
                // suivant (galaxie, sélecteur) reprend le relais — bloquant chez lui.
                if (user) void ensureBeneficiariesSynced();
                history.replace(user ? '/home' : ximiErrorRedirect());
            } finally {
                ximiSignInInFlight = false;
            }
        };
        run();
    }, [auth.isAuthenticated, auth.user]);
}
